import type { StateCreator } from "zustand";
import type { PrizeDrop } from "@/campaigns/modules/prizedrop/types";
import type { PrizeDropPrizesSchema } from "@/types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

export interface PrizeDropSlice {
  /** Assembled view objects (campaigns ⋈ prize_drop_prizes), rebuilt on stream change. */
  prizeDrops: PrizeDrop[];
  setPrizeDrops: (prizeDrops: PrizeDrop[]) => void;
  clearPrizeDrops: () => void;

  /** Raw `prize_drop_prizes` stream, keyed by campaign key (the volatile tail). */
  prizeDropPrizesById: Record<CampaignKey, PrizeDropPrizesSchema>;
  prizeDropPrizesStream: CampaignStreamState;
  setPrizeDropPrizes: (prizes: PrizeDropPrizesSchema[]) => void;
  setPrizeDropPrizesSubscribed: (subscribed: boolean) => void;
  setPrizeDropPrizesLoading: (loading: boolean) => void;
  clearPrizeDropPrizes: () => void;

  /** Until when (epoch ms) each prize drop runs hot, by campaign id: the
   *  `prize_drop_hot_period` hook (turnover ≥ 2× the average), shown on the
   *  in-game button for its TTL. */
  prizeDropHotUntil: Record<number, number>;
  setPrizeDropHot: (campaignId: number, until: number | null) => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createPrizeDropSlice: StateCreator<PrizeDropSlice> = (set) => ({
  prizeDrops: [],
  setPrizeDrops: (prizeDrops) => set({ prizeDrops }),
  clearPrizeDrops: () => set({ prizeDrops: [] }),

  prizeDropPrizesById: {},
  prizeDropPrizesStream: initialStream,
  setPrizeDropPrizes: (prizes) =>
    set(() => {
      const byId: Record<CampaignKey, PrizeDropPrizesSchema> = {};
      for (const p of prizes) byId[campaignKey(p)] = p;
      return { prizeDropPrizesById: byId };
    }),
  setPrizeDropPrizesSubscribed: (subscribed) =>
    set((state) => ({
      prizeDropPrizesStream: {
        ...state.prizeDropPrizesStream,
        subscribed,
        loading: false,
      },
    })),
  setPrizeDropPrizesLoading: (loading) =>
    set((state) => ({
      prizeDropPrizesStream: { ...state.prizeDropPrizesStream, loading },
    })),
  prizeDropHotUntil: {},
  setPrizeDropHot: (campaignId, until) =>
    set((state) => {
      const next = { ...state.prizeDropHotUntil };
      if (until === null) delete next[campaignId];
      else next[campaignId] = until;
      return { prizeDropHotUntil: next };
    }),
  clearPrizeDropPrizes: () =>
    set({ prizeDropPrizesById: {}, prizeDropPrizesStream: initialStream }),
});

/** Resolve a prize-drop campaign by its custom_id or numeric id (as string). */
export function selectPrizeDropById(
  state: AppStore,
  id: string
): PrizeDrop | undefined {
  return state.prizeDrops.find((pd) => matchesKey(pd.campaign, id));
}
